// Closing-only read-only slide-out for a §800 fee discrepancy: the submitted
// doc-order amount vs the live Encompass value, the current invoices, and the
// last disclosed LE/GFE — with the two resolves the Closer can take. Ported from
// FeeTable.jsx EncompassDiffSheet. Rendered INLINE (NO portal — R4 #5): the tab
// lives inside the Radix LoanDetailsSheet, whose modal lock sets
// pointer-events:none on document.body, so a body portal goes fully inert
// (the same Jennifer bugs 4/6 class ReconcileSheet already fixed).
import { useEffect } from 'react';
import { Icon } from '../atoms';
import { fmtMoney } from '../format';
import { invoiceTotal, DO_TOLERANCE, type DocOrderFee } from '../core';

interface EncompassDiffSheetProps {
  fee: DocOrderFee;
  onUseEncompass: () => void;
  onKeepSubmitted: () => void;
  onClose: () => void;
}

export function EncompassDiffSheet({ fee, onUseEncompass, onKeepSubmitted, onClose }: EncompassDiffSheetProps) {
  const submitted = Number(fee.amount) || 0;
  const enc = Number(fee.encompassAmount) || 0;
  const invTotal = invoiceTotal(fee);
  const disclosed = fee.lastDisclosedLE !== null ? Number(fee.lastDisclosedLE) : null;
  const encMismatch = fee.encompassAmount !== null && Math.abs(enc - submitted) >= DO_TOLERANCE.discrepancy;
  const invMismatch = invTotal > DO_TOLERANCE.costToCure && Math.abs(invTotal - submitted) >= DO_TOLERANCE.discrepancy;
  const discMismatch = disclosed !== null && Math.abs(disclosed - submitted) >= DO_TOLERANCE.discrepancy;
  const M = (n: number) => fmtMoney(n, true);
  // Difference = compared − doc-order; a shortfall renders like −$30.
  const D = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${M(Math.abs(n))}`;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="recon-overlay" data-doc-order>
      <div className="recon-scrim" onClick={onClose} />
      <aside className="recon-sheet" role="dialog" aria-label={`Fee Discrepancy — ${fee.name}`}>
        <header className="recon-hd">
          <div>
            <div className="recon-eyebrow ctc">
              <Icon name="unlink" size={12} />Fee Discrepancy
            </div>
            <div className="recon-title">
              {fee.name}
              {fee.payee ? <span className="recon-payee"> · {fee.payee}</span> : null}
            </div>
          </div>
          <button className="recon-close" onClick={onClose} aria-label="Close">
            <Icon name="x" size={15} />
          </button>
        </header>

        {/* 1 — doc order vs Encompass §800 */}
        <div className="ctc-sheet-block">
          <span className="fee-ctc-lbl">
            Doc order vs Encompass §800{' '}
            {encMismatch ? <span className="enc-diff-tag bad">Differs</span> : <span className="enc-diff-tag ok">Matches</span>}
          </span>
          <div className="recon-compare" style={{ borderBottom: 'none' }}>
            <div className="recon-cmp-row">
              <span>Submitted on doc order</span>
              <b>{M(submitted)}</b>
            </div>
            <div className="recon-cmp-row">
              <span>Encompass §800</span>
              <b>{fee.encompassAmount !== null ? M(enc) : '—'}</b>
            </div>
            <div className={`recon-cmp-row delta ${encMismatch ? 'over' : 'ok'}`}>
              <span>Difference</span>
              <b>{encMismatch ? D(enc - submitted) : 'Matches'}</b>
            </div>
          </div>
          {encMismatch && (
            <div className="enc-sheet-actions">
              <button type="button" className="enc-vest-btn primary" onClick={onUseEncompass}>
                <Icon name="link" size={13} />Use Encompass ({M(enc)})
              </button>
              <button type="button" className="enc-vest-btn" onClick={onKeepSubmitted}>
                <Icon name="check" size={13} />Keep submitted ({M(submitted)})
              </button>
            </div>
          )}
        </div>

        {/* 2 — doc order vs invoices */}
        <div className="ctc-sheet-block">
          <span className="fee-ctc-lbl">
            Doc order vs invoices{' '}
            {invMismatch ? (
              <span className="enc-diff-tag bad">Differs</span>
            ) : invTotal > DO_TOLERANCE.costToCure ? (
              <span className="enc-diff-tag ok">Matches</span>
            ) : null}
          </span>
          <div className="recon-compare" style={{ borderBottom: 'none' }}>
            <div className="recon-cmp-row">
              <span>Submitted on doc order</span>
              <b>{M(submitted)}</b>
            </div>
            <div className="recon-cmp-row">
              <span>Invoice total ({(fee.invoices ?? []).length})</span>
              <b>{invTotal > DO_TOLERANCE.costToCure ? M(invTotal) : '—'}</b>
            </div>
            {invTotal > DO_TOLERANCE.costToCure && (
              <div className={`recon-cmp-row delta ${invMismatch ? 'over' : 'ok'}`}>
                <span>Difference</span>
                <b>{invMismatch ? D(invTotal - submitted) : 'Matches'}</b>
              </div>
            )}
          </div>
          {invMismatch && <p className="ctc-sheet-note" style={{ marginTop: 4 }}>Invoices in the file no longer match the submitted fee. Edit the fee to resolve the discrepancy.</p>}
        </div>

        {/* 3 — doc order vs last disclosed */}
        <div className="ctc-sheet-block">
          <span className="fee-ctc-lbl">
            Doc order vs Last disclosed{' '}
            {disclosed !== null ? (
              discMismatch ? (
                <span className="enc-diff-tag bad">Differs</span>
              ) : (
                <span className="enc-diff-tag ok">Matches</span>
              )
            ) : null}
          </span>
          <div className="recon-compare" style={{ borderBottom: 'none' }}>
            <div className="recon-cmp-row">
              <span>Submitted on doc order</span>
              <b>{M(submitted)}</b>
            </div>
            <div className="recon-cmp-row">
              <span>
                Last disclosed fee <span className="recon-cmp-src">LE / GFE</span>
              </span>
              <b>{disclosed !== null ? M(disclosed) : '—'}</b>
            </div>
            {disclosed !== null && (
              <div className={`recon-cmp-row delta ${discMismatch ? 'over' : 'ok'}`}>
                <span>Difference</span>
                <b>{discMismatch ? D(disclosed - submitted) : 'Matches'}</b>
              </div>
            )}
          </div>
        </div>

        <div className="recon-stamp">
          <Icon name="alertCircle" size={11} /> Resolving records your choice here — the kept amount is written to Encompass §800 when the order is sent to the LO.
        </div>
      </aside>
    </div>
  );
}
